import {Link2, Tag, UserCircle2} from 'lucide-react'
import {useI18n} from '@/i18n'
import {Stack} from '@/components/shared/Stack'
import {Card, CardContent, CardHeader, CardTitle} from '@/components/ui/card'
import {useAppConfig} from '@/hooks/use-app-config'
import {RelationshipLayoutRenderer} from '@/components/form-layout/renderer/RelationshipLayoutRenderer'
import {AttributeLayoutRenderer} from '@/components/form-layout/renderer/AttributeLayoutRenderer'
import {IdentityManagerPicker} from '@/features/onboarding/components/IdentityManagerPicker'
import type {OnboardingFormState, RelationshipFormEntry} from '../types'

const MANAGED_BY_TYPE = 'MANAGED_BY'

interface OrganizationStepProps {
    form: OnboardingFormState
    onUpdateMultiple: (fields: Partial<OnboardingFormState>) => void
    onSetPartyAttributes: (attrs: OnboardingFormState['partyAttributes']) => void
}

/**
 * Step 2: Organization — dynamic relationships from schema, party attributes.
 * Uses Card sections matching the detail page pattern (icon + title).
 * Relationships and attributes are rendered via FormLayoutRenderer components
 * which apply any saved layout or fall back to default rendering.
 *
 * <p>Epic 986 Phase D — the {@code MANAGED_BY} edge is now Identity-to-Identity.
 * It's rendered by {@link IdentityManagerPicker} (an Identity Combobox) and
 * skipped by the {@code RelationshipLayoutRenderer} so it doesn't double-render
 * via the Party schema.
 */
export function OrganizationStep({form, onUpdateMultiple, onSetPartyAttributes}: OrganizationStepProps) {
    const i18n = useI18n()
    const {partyRelationshipSchema} = useAppConfig()

    const handleRelationshipChange = (
        relationshipType: string,
        targetId: string | null,
        targetLabel: string,
        secondaryLabel?: string
    ) => {
        const updated = [...form.relationships]
        const idx = updated.findIndex((r) => r.relationshipType === relationshipType)
        const entry: RelationshipFormEntry = {relationshipType, targetId, targetLabel, secondaryLabel}
        if (idx >= 0) {
            updated[idx] = entry
        } else {
            updated.push(entry)
        }
        onUpdateMultiple({relationships: updated})
    }

    const managedByEntry = form.relationships.find((r) => r.relationshipType === MANAGED_BY_TYPE)

    // Build template context from form state
    const templateContext: Record<string, string> = {}
    for (const rel of form.relationships) {
        if (rel.targetLabel) {
            // Map relationship types to template keys
            // e.g. DEPARTMENT_MEMBERSHIP → department
             
            const key = rel.relationshipType.toLowerCase().replace(/_membership$/, '')
            templateContext[key] = rel.targetLabel
        }
    }
    if (form.templateKey) {
        templateContext.templateKey = form.templateKey
    }

    const schemas = partyRelationshipSchema ?? []
    const hasOtherRelationships = schemas.some(
        (s) => s.fromPartyType === form.partyType && s.relationshipType !== MANAGED_BY_TYPE)
    const showManagerPicker = form.partyType === 'PERSON'

    return (
        <Stack gap="lg">
            {/* Manager (Identity-to-Identity, MANAGED_BY) Section — epic 986 Phase D */}
            {showManagerPicker && (
                <Card>
                    <CardHeader>
                        <CardTitle className="items-center gap-2" data-testid="card-title-manager">
                            <UserCircle2 className="h-5 w-5"/>
                            {i18n.onboarding.organization.managerSection}
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <IdentityManagerPicker
                            selectedIdentityId={managedByEntry?.targetId ?? null}
                            selectedPrimaryLabel={managedByEntry?.targetLabel ?? ''}
                            selectedSecondaryLabel={managedByEntry?.secondaryLabel ?? ''}
                            onChange={(id, primary, secondary) =>
                                handleRelationshipChange(MANAGED_BY_TYPE, id, primary, secondary)}
                        />
                    </CardContent>
                </Card>
            )}

            {/* Other Relationships Section (renders MANAGED_BY-excluded schema rows) */}
            {hasOtherRelationships && (
                <Card>
                    <CardHeader>
                        <CardTitle className="items-center gap-2" data-testid="card-title-relationships">
                            <Link2 className="h-5 w-5" />
                            {i18n.onboarding.organization.relationships}
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <RelationshipLayoutRenderer
                            /* eslint-disable-next-line i18next/no-literal-string */
                            useCaseKey="party-relationship"
                            partyType={form.partyType}
                            relationships={form.relationships}
                            onRelationshipChange={handleRelationshipChange}
                            partyRelationshipSchema={schemas}
                            templateContext={templateContext}
                            skipRelationshipTypes={[MANAGED_BY_TYPE]}
                        />
                    </CardContent>
                </Card>
            )}

            {/* Attributes Section */}
            <Card>
                <CardHeader>
                    <CardTitle className="items-center gap-2" data-testid="card-title-organization-attributes">
                        <Tag className="h-5 w-5" />
                        {i18n.onboarding.organization.additionalDetails}
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    <AttributeLayoutRenderer
                        /* eslint-disable-next-line i18next/no-literal-string */
                        useCaseKey="party-attribute"
                         
                        entityType="PARTY"
                        typeCode={form.partyType}
                        values={form.partyAttributes}
                        onChange={onSetPartyAttributes}
                        existingValues={form.existingPartyData?.partyAttributes}
                        templateContext={templateContext}
                    />
                </CardContent>
            </Card>
        </Stack>
    )
}
